import DriverIcon from 'src/assets/icons/driver-front.svg'
import BGCIcon from 'src/assets/icons/bgc.svg'
import {
  BlogMajor,
  CustomersMajor,
  FinancesMajor,
  FraudProtectMajor,
  JobsMajor,
  NoteMajor,
  PageDownMajor,
  QuestionMarkMajor,
  SecureMajor,
  ShipmentMajor,
  TextBlockMajor,
} from '@shopify/polaris-icons'
import { GSIcon } from 'src/components/shared/GSIcon'
import { CollectDataStepDataMapItem } from 'src/interfaces/collectData'
import { CollectDataStepType } from './enums'

export const COLLECT_DATA_STEP_TIME_TO_COMPLETE = {
  [CollectDataStepType.DriverLicense]: 2,
  [CollectDataStepType.VehicleInsurance]: 3,
  [CollectDataStepType.InsuranceDeclaration]: 1,
  [CollectDataStepType.VehicleRegistration]: 1,
  [CollectDataStepType.Vaccination]: 1,
  [CollectDataStepType.BGC]: 3,
  [CollectDataStepType.StitchPayment]: 3,
  [CollectDataStepType.InsuranceBeneficiary]: 1,
  [CollectDataStepType.SignContract]: 3,
  [CollectDataStepType.MVR]: 2,
  [CollectDataStepType.Form]: 2,
  [CollectDataStepType.EIN]: 2,
  [CollectDataStepType.InsuranceCoverage]: 2,
  [CollectDataStepType.LMSTraining]: 5,
  [CollectDataStepType.MCNumber]: 3,
  [CollectDataStepType.DOTNumber]: 3,
  [CollectDataStepType.PaymentMethod]: 3,
  [CollectDataStepType.PIICollection]: 3,
}

export const collectDataStepDataMap: {
  [key in string]: (options: { optional: boolean }) => CollectDataStepDataMapItem
} = {
  [CollectDataStepType.DriverLicense]: () => ({
    icon: <img src={DriverIcon} />,
    title: "Driver's License",
    text: "Provide your basic details and upload your driver's license for swift verification.",
    finishTime: COLLECT_DATA_STEP_TIME_TO_COMPLETE[CollectDataStepType.DriverLicense],
  }),
  [CollectDataStepType.VehicleInsurance]: () => ({
    icon: <ShipmentMajor width={20} />,
    title: 'Vehicle and insurance',
    text: 'Input your vehicle details and upload your insurance card.',
    finishTime: COLLECT_DATA_STEP_TIME_TO_COMPLETE[CollectDataStepType.VehicleInsurance],
  }),
  [CollectDataStepType.InsuranceDeclaration]: () => ({
    icon: <NoteMajor height={20} />,
    title: 'Insurance declaration page',
    text: 'Submit your detailed Insurance Declarations Page with coverage limits.',
    finishTime: COLLECT_DATA_STEP_TIME_TO_COMPLETE[CollectDataStepType.InsuranceDeclaration],
  }),
  [CollectDataStepType.VehicleRegistration]: () => ({
    icon: <TextBlockMajor height={20} width={20} />,
    title: 'Vehicle registration',
    text: 'Submit your vehicle registration by uploading a photo of it.',
    finishTime: COLLECT_DATA_STEP_TIME_TO_COMPLETE[CollectDataStepType.VehicleRegistration],
  }),
  [CollectDataStepType.Vaccination]: () => ({
    icon: <SecureMajor height={20} width={20} />,
    title: 'Vaccination information',
    text: 'Enter details about your most recent vaccination.',
    finishTime: COLLECT_DATA_STEP_TIME_TO_COMPLETE[CollectDataStepType.Vaccination],
  }),
  [CollectDataStepType.BGC]: () => ({
    icon: <img src={BGCIcon} />,
    title: 'Background check',
    text: 'Run through our partner, Yardstik.',
    finishTime: COLLECT_DATA_STEP_TIME_TO_COMPLETE[CollectDataStepType.BGC],
  }),
  [CollectDataStepType.StitchPayment]: () => ({
    icon: <FinancesMajor height={20} width={20} />,
    title: 'Get paid!',
    text: 'Choose your preferred way to get paid!',
    finishTime: COLLECT_DATA_STEP_TIME_TO_COMPLETE[CollectDataStepType.StitchPayment],
  }),
  [CollectDataStepType.InsuranceBeneficiary]: () => ({
    icon: <CustomersMajor height={18} width={16} />,
    title: 'Insurance beneficiary',
    text: 'Set your beneficiary for insurance policies.',
    finishTime: COLLECT_DATA_STEP_TIME_TO_COMPLETE[CollectDataStepType.InsuranceBeneficiary],
  }),
  [CollectDataStepType.SignContract]: () => ({
    icon: <BlogMajor height={20} width={19} />,
    title: 'Sign the contract',
    text: 'Review the contract and complete the signing process.',
    finishTime: COLLECT_DATA_STEP_TIME_TO_COMPLETE[CollectDataStepType.SignContract],
  }),
  [CollectDataStepType.MVR]: ({ optional }) => ({
    icon: <PageDownMajor width={20} />,
    title: 'Upload MVR' + (optional ? ' (optional)' : ''),
    text: 'Submit your detailed motor vehicle record.',
    finishTime: COLLECT_DATA_STEP_TIME_TO_COMPLETE[CollectDataStepType.MVR],
  }),
  [CollectDataStepType.Form]: () => ({
    icon: <QuestionMarkMajor width={20} />,
    title: 'Questionnaire',
    text: "Let's see if you're a good fit.",
    finishTime: COLLECT_DATA_STEP_TIME_TO_COMPLETE[CollectDataStepType.Form],
  }),
  [CollectDataStepType.EIN]: () => ({
    icon: <JobsMajor width={20} />,
    title: 'Business entity',
    text: 'Provide business information, if applicable.',
    finishTime: COLLECT_DATA_STEP_TIME_TO_COMPLETE[CollectDataStepType.EIN],
  }),
  [CollectDataStepType.InsuranceCoverage]: () => ({
    icon: <FraudProtectMajor width={20} />,
    title: 'Insurance coverage',
    text: 'Choose your insurance option.',
    finishTime: COLLECT_DATA_STEP_TIME_TO_COMPLETE[CollectDataStepType.InsuranceCoverage],
  }),
  [CollectDataStepType.LMSTraining]: () => ({
    icon: <NoteMajor width={20} />,
    title: 'Certification Step',
    text: 'Start your certification with our verified partner.',
    finishTime: COLLECT_DATA_STEP_TIME_TO_COMPLETE[CollectDataStepType.LMSTraining],
  }),
  [CollectDataStepType.MCNumber]: ({ optional }) => ({
    icon: <ShipmentMajor width={20} />,
    title: 'Motor Carrier Number' + (optional ? ' (optional)' : ''),
    text: 'Validate Motor Carrier Number.',
    finishTime: COLLECT_DATA_STEP_TIME_TO_COMPLETE[CollectDataStepType.MCNumber],
  }),
  [CollectDataStepType.DOTNumber]: ({ optional }) => ({
    icon: <ShipmentMajor width={20} />,
    title: 'DOT/USDOT Number' + (optional ? ' (optional)' : ''),
    text: 'Submit your DOT/USDOT Number.',
    finishTime: COLLECT_DATA_STEP_TIME_TO_COMPLETE[CollectDataStepType.DOTNumber],
  }),
  [CollectDataStepType.PaymentMethod]: () => ({
    icon: <GSIcon iconName="gs_wallet_3" height={20} width={20} />,
    title: 'Get paid!',
    text: 'Choose your preferred way to get paid!',
    finishTime: COLLECT_DATA_STEP_TIME_TO_COMPLETE[CollectDataStepType.PaymentMethod],
    hideSubTitle: true,
  }),
  [CollectDataStepType.PIICollection]: () => ({
    icon: <TextBlockMajor className="w-5 h-5 shrink-0" />,
    title: 'Social Security Number',
    text: 'Enter your Social Security Number (SSN), current address, and upload a work authorization document if required.',
    finishTime: COLLECT_DATA_STEP_TIME_TO_COMPLETE[CollectDataStepType.PIICollection],
  }),
}
